// Карточка контакта — тот же живой каркас, что у сделки и компании (components/card):
// слева вкладки «Общие»/«Сделки», справа лента. Полосы стадий нет — у контакта нет воронки.
//
// Зеркальные поля Bitrix (ФИО, компания, должность, телефоны) — с капсулой и без правки:
// на изменения контакта в Bitrix навешаны бизнес-процессы, источник правды там. Живая
// часть — своя схема ядра: лента (у контактов есть и зеркальная история — дела/звонки
// Bitrix, замер 30.07.2026: 3 341 запись на весь справочник), комментарии и «Наши поля»
// из конструктора.
// Стыковка — api/cardLiveEntities.ts; свой id приезжает в `_OWN.id` ответа /own/contact.
//
// Судно, открытое как контакт, остаётся особым случаем: это одна запись Bitrix, и карточка
// уводит на /vessels/:id, а живую часть не трогает — свой id судна лежит в ДРУГОЙ таблице
// (vessel, не person), и лента «контакта» по нему была бы чужой лентой.
// РАЗДЕЛЫ ПОЛЕЙ — ПО ЖИВОМУ BITRIX. У контакта и судна в Bitrix ОДИН макет из пяти
// разделов («О контакте», «Верификации», «короткая верификация», «дополнительно»,
// «Документы БТО» — снят из DOM 31.07.2026, api/entityLayouts.ts). У человека судовые
// разделы без единого значения прячутся — по данным зеркала они заполнены у судов,
// а у людей лишь единичными остатками. Сырые UF-поля своя база не отдаёт — карточка
// доспрашивает их у зеркала (useСырьёКарточкиBitrix).
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import {
  добавитьКомментарий, ownIdКарточки, почемуНедоступно, событияЛенты,
  сохранитьЗначение, словами, useЛентаКарточки, useНашиПоля, КЛЮЧ_НАШИХ_ПОЛЕЙ,
} from '../api/cardLiveEntities'
import {
  МАКЕТ_КОНТАКТА, заметкаСырья, слитьДляРаскладки, useСырьёКарточкиBitrix,
} from '../api/entityLayouts'
import { useContact, useDeals, useFields, useRefs, useRightsApi } from '../api/hooks'
import { ПОДСКАЗКА_НЕТ_СОЗДАНИЯ, подсказкаПустойДаты } from '../api/istochnik'
import { CardShell } from '../components/card/CardShell'
import type { CardTab } from '../components/card/CardShell'
import type { CardField } from '../components/card/FieldRow'
import { FieldSection } from '../components/card/FieldSection'
import { Timeline } from '../components/card/Timeline'
import type { CommentResult, TimelineEvent } from '../components/card/Timeline'
import { EntityLink } from '../components/EntityLink'
import { OwnFieldsSection } from '../components/OwnFieldsSection'
import { QueryState } from '../components/QueryState'
import { contactName, entityPath, isVesselContact } from '../lib/entities'
import { fmtDate, fmtMoney, fmtText } from '../lib/format'

import { ПРОЧЕРК, РазделыBitrix } from '../components/cardEntities'

/** Сколько связанных записей показываем. Полный список — в своём разделе. */
const RELATED = 10

type Вкладка = 'main' | 'deals'

export default function ContactCard() {
  const { id } = useParams<{ id: string }>()
  const q = useContact(id)
  const refs = useRefs()
  const f = useFields('contact')
  const rights = useRightsApi()
  const qc = useQueryClient()
  const [вкладка, setВкладка] = useState<Вкладка>('main')

  // Сделки контакта. select ограничиваем: карточке не нужен весь raw на каждую строку.
  const deals = useDeals({
    filter: id ? { CONTACT_ID: id } : {},
    order: { DATE_MODIFY: 'DESC' },
    select: ['ID', 'TITLE', 'STAGE_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'DATE_MODIFY'],
  })

  const c = q.data
  const vessel = c ? isVesselContact(c) : false
  // У судна свой id — из таблицы судов, а не людей: живую часть контакта не спрашиваем.
  const ownId = vessel ? null : ownIdКарточки(c)

  // Сырьё зеркала: полная запись контакта со всеми UF-полями (верификации, документы БТО).
  const сырьё = useСырьёКарточкиBitrix('crm.contact.get', id)

  const лента = useЛентаКарточки('контакт', ownId)
  const наши = useНашиПоля('контакт', ownId)

  const [сессионные, setСессионные] = useState<TimelineEvent[]>([])
  const добавитьСобытие = (с: Omit<TimelineEvent, 'id' | 'at'>) =>
    setСессионные((были) => [
      ...были,
      { ...с, id: `s-${были.length}`, at: new Date().toISOString() },
    ])

  const события = useMemo<TimelineEvent[]>(() => {
    const из: TimelineEvent[] = [...сессионные, ...событияЛенты(лента.data?.pages)]
    if (c?.DATE_CREATE) {
      из.push({
        id: 'created', at: String(c.DATE_CREATE), kind: 'bitrix',
        text: 'Контакт заведён', origin: 'bitrix',
      })
    }
    return из
  }, [сессионные, лента.data, c])

  // У судна живая часть — на карточке судна (другая таблица своей схемы): говорить
  // «не перенесено» про запись, которая переносится судном, было бы неправдой.
  const заметкаЛенты = vessel
    ? 'Лента судна живёт на карточке судна.'
    : почемуНедоступно(ownId, лента.error)

  const отправитьКомментарий = async (текст: string): Promise<CommentResult> => {
    if (ownId === null) return { ok: false, почему: заметкаЛенты }
    try {
      const с = await добавитьКомментарий('контакт', ownId, текст)
      добавитьСобытие({ kind: 'comment', text: String(с.text ?? текст), origin: 'crm' })
      return { ok: true }
    } catch (e) {
      return { ok: false, почему: словами(e) }
    }
  }

  const сохранитьНашеПоле = async (код: string, значение: unknown) => {
    await сохранитьЗначение('контакт', ownId!, код, значение)
    const подпись = наши.data?.custom.find((п) => п.code === код)?.label ?? код
    добавитьСобытие({ kind: 'field', text: `Изменено поле «${подпись}»`, origin: 'crm' })
    await qc.invalidateQueries({ queryKey: [КЛЮЧ_НАШИХ_ПОЛЕЙ, 'контакт', ownId] })
  }

  const вкладки: CardTab[] = [
    { key: 'main', label: 'Общие' },
    { key: 'deals', label: deals.data && !deals.data.stale ? `Сделки · ${deals.data.total}` : 'Сделки' },
  ]

  return (
    <>
      <div className="page-head">
        <h1>{c ? contactName(c) : `Контакт #${id}`}</h1>
        <div className="spacer" />
        <Link to="/contacts">← к списку</Link>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        {c && (
          <>
            {/* Судно и контакт — одна запись Bitrix. Показать её как обычного человека
                значит спрятать IMO, порт и сделки по судну — уводим на нужную карточку. */}
            {vessel && (
              <div className="notice">
                Это не человек, а судно: в Bitrix суда хранятся контактами с фамилией «Судно».{' '}
                <Link to={entityPath('vessel', c.ID)}>Открыть карточку судна →</Link>
              </div>
            )}

            <CardShell
              head={
                <div className="cshell-head">
                  <div className="cshell-title">{contactName(c)}</div>
                  <div className="spacer" />
                  {c.POST ? <span className="muted">{fmtText(c.POST)}</span> : null}
                </div>
              }
              tabs={вкладки}
              active={вкладка}
              onTab={(k) => setВкладка(k as Вкладка)}
              left={
                <>
                  {вкладка === 'main' && (
                    <Поля c={c} f={f} refs={refs} rights={rights} ownId={ownId} наши={наши}
                          сырьё={сырьё} vessel={vessel}
                          почемуНаши={vessel ? 'У судна свои поля — на карточке судна.' : undefined}
                          onSaveExt={rights.canWrite && ownId !== null ? сохранитьНашеПоле : undefined} />
                  )}
                  {вкладка === 'deals' && <Сделки deals={deals} refs={refs} />}
                </>
              }
              right={
                <>
                  <h2>Лента</h2>
                  <Timeline
                    events={события}
                    onComment={rights.canWrite && ownId !== null ? отправитьКомментарий : undefined}
                    note={заметкаЛенты}
                    more={{
                      has: !!лента.hasNextPage,
                      loading: лента.isFetchingNextPage,
                      onMore: () => { void лента.fetchNextPage() },
                    }}
                    emptyText="Событий в ленте нет"
                  />
                </>
              }
            />
          </>
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════ вкладка «Общие» ═══════════════════════

/**
 * Разделы — по живому Bitrix (МАКЕТ_КОНТАКТА). Всё зеркальное: капсула, без правки.
 * Судовые разделы у человека прячутся, когда пусты (см. РАЗДЕЛ_ВСЕГДА в entityLayouts).
 * Плюс свои разделы в конце: «Служебные» (даты) и «Наши поля» из конструктора.
 *
 * Две строки первого раздела — виджеты Bitrix, собираем сами: КОМПАНИЯ — ссылка на
 * карточку (resolve — одиночное место, догрузить название допустимо), РЕКВИЗИТЫ — у
 * людей лежат в отдельном справочнике Bitrix (bx_requisites) и в карточку не приходят.
 */
function Поля({ c, f, refs, rights, ownId, наши, сырьё, vessel, почемуНаши, onSaveExt }: {
  c: NonNullable<ReturnType<typeof useContact>['data']>
  f: ReturnType<typeof useFields>
  refs: ReturnType<typeof useRefs>
  rights: ReturnType<typeof useRightsApi>
  ownId: number | null
  наши: ReturnType<typeof useНашиПоля>
  сырьё: ReturnType<typeof useСырьёКарточкиBitrix>
  vessel: boolean
  почемуНаши?: string
  onSaveExt?: (code: string, value: unknown) => Promise<void>
}) {
  const запись = слитьДляРаскладки(c, сырьё.data?.запись)

  const особые: Record<string, CardField | null> = {
    COMPANY_ID: {
      code: 'COMPANY_ID', label: f.titleOr('COMPANY_ID', 'Компания'), type: 'text',
      origin: 'bitrix', readOnly: true, value: c.COMPANY_ID || null,
      display: <EntityLink kind="company" id={c.COMPANY_ID} resolve />,
    },
    REQUISITES: {
      code: 'REQUISITES', label: 'Реквизиты', type: 'text', origin: 'bitrix', readOnly: true,
      value: null, display: ПРОЧЕРК,
      hint: 'реквизиты Bitrix живут в отдельном справочнике (bx_requisites) и в ответ карточки не входят',
    },
  }

  const служебные: CardField[] = [
    { code: 'POST', label: f.titleOr('POST', 'Должность'), type: 'text', value: c.POST,
      origin: 'bitrix', readOnly: true },
    { code: 'SECOND_NAME', label: f.titleOr('SECOND_NAME', 'Отчество'), type: 'text',
      value: c.SECOND_NAME, origin: 'bitrix', readOnly: true },
    { code: 'DATE_CREATE', label: 'Создан', type: 'date', value: c.DATE_CREATE,
      origin: 'bitrix', readOnly: true, display: fmtDate(c.DATE_CREATE, true),
      hint: подсказкаПустойДаты(c.DATE_CREATE, ПОДСКАЗКА_НЕТ_СОЗДАНИЯ) },
    { code: 'DATE_MODIFY', label: 'Изменён', type: 'date', value: c.DATE_MODIFY,
      origin: 'bitrix', readOnly: true, display: fmtDate(c.DATE_MODIFY, true),
      hint: подсказкаПустойДаты(c.DATE_MODIFY) },
  ]

  return (
    <div className="card">
      {!f.loaded && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Справочник полей недоступен — названия показаны по снимку раскладки 31.07.2026.
        </div>
      )}
      {f.loaded && f.stale && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Справочник полей давно не обновлялся: если поле недавно переименовали,
          название может отставать.
        </div>
      )}
      <РазделыBitrix макет={МАКЕТ_КОНТАКТА} запись={запись} f={f} refs={refs}
                     особые={особые} прятатьПустые={!vessel} заметка={заметкаСырья(сырьё)} />
      <FieldSection title="Служебные" fields={служебные} />
      <OwnFieldsSection сущностьМн="контактов" ownId={ownId} наши={наши}
                        rights={rights} onSave={onSaveExt} почему={почемуНаши} />
    </div>
  )
}

// ═══════════════════════ вкладка «Сделки» ═══════════════════════

function Сделки({ deals, refs }: {
  deals: ReturnType<typeof useDeals>
  refs: ReturnType<typeof useRefs>
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Сделки</h2>
        <div className="spacer" />
        <span className="muted">
          {deals.data && !deals.data.stale ? `всего: ${deals.data.total}` : ''}
        </span>
      </div>
      {/* Сужение правами: «Сделок с этим контактом нет» и «его сделки ведёт
          другой отдел» — разные ответы, и второй меняет решение. */}
      <QueryState isLoading={deals.isLoading} error={deals.error}
                  stale={deals.data?.stale} ageMin={deals.data?.ageMin}
                  scope={deals.data?.scope}>
        {(deals.data?.items ?? []).length === 0 ? (
          <div className="muted">
            Сделок с этим контактом нет.
            {' '}Учтите: в сделке контакт хранится одним полем CONTACT_ID —
            если человека привязали не им, сделка сюда не попадёт.
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Название</th>
                  <th className="nowrap">Стадия</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {(deals.data?.items ?? []).slice(0, RELATED).map((d) => (
                  <tr key={d.ID}>
                    <td className="mono nowrap" data-label="ID">
                      <EntityLink kind="deal" id={d.ID} title={d.ID} />
                    </td>
                    <td data-label="Название">
                      <EntityLink kind="deal" id={d.ID} title={fmtText(d.TITLE)} />
                    </td>
                    <td className="nowrap" data-label="Стадия">
                      <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>
                        {refs.stageName(d.STAGE_ID)}
                      </span>
                    </td>
                    <td className="num" data-label="Сумма">{fmtMoney(d.OPPORTUNITY, d.CURRENCY_ID || 'RUB')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(deals.data?.total ?? 0) > RELATED && (
          <div className="hint" style={{ marginTop: 10 }}>
            Показаны первые {RELATED} из {deals.data?.total} по дате изменения.
          </div>
        )}
      </QueryState>
    </div>
  )
}
